import { ILabel, ILabelType } from 'annotation-tools/annotation-canvas';
import {
  combineLatestTyped,
  promiseState,
  useDataStream,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo } from 'react';
import { map, startWith, switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../../global-state/database/common/map-latest';
import { modelImageAnnotationListOf } from '../../../global-state/database/model-annotation-list';
import { modelImageListOf } from '../../../global-state/database/model-image-list';
import { labelListOf } from '../../../global-state/database/model-label-list';
import { imageId$, modelId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { IAnnotationType } from '../../../types/record/annotation';
import { IImageInfoType } from '../../../types/record/image-info';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ModelImageRootSub } from './model-image-root-sub';

export const ModelImageRoot: FC<{}> = memo(() => {
  const labelList$ = useDataStream<ListWithPromiseState<ILabelType>>(
    listWithPromiseStateInit(),
    modelId$.pipe(
      switchMap(labelListOf),
      map(list => mapListLatest(list, l => l.map(ILabel)))
    )
  );

  const imageList$ = useDataStream<ListWithPromiseState<IImageInfoType>>(
    listWithPromiseStateInit(),
    modelId$.pipe(switchMap(modelImageListOf))
  ); // TODO switch直後にリセットする

  const annotationList$ = useDataStream<ListWithPromiseState<IAnnotationType>>(
    listWithPromiseStateInit(),
    combineLatestTyped(modelId$, imageId$).pipe(
      switchMap(([modelId, imageId]) =>
        modelImageAnnotationListOf(modelId, imageId).pipe(
          startWith(listWithPromiseStateInit<IAnnotationType>())
        )
      )
    )
  );

  /* extracted values */
  const imageList = useStreamValue(imageList$);
  const labelList = useStreamValue(labelList$);
  const annotationList = useStreamValue(annotationList$);

  useLoadingPage(
    imageList?.status === 'pending' ||
      labelList?.status === 'pending' ||
      annotationList?.status === 'pending'
  );

  return (
    <>
      {imageList === undefined ||
      labelList === undefined ||
      annotationList === undefined ? (
        undefined
      ) : labelList.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.labelList} />
      ) : annotationList.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.annotationList} />
      ) : imageList.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.modelImageList} />
      ) : (
        <ModelImageRootSub
          imageList={imageList.value.latest}
          labelList={labelList.value.latest}
          annotationList={annotationList.value.latest}
        />
      )}
    </>
  );
});

ModelImageRoot.displayName = 'ModelImageRoot';
